import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  faAward, faBan, faChartLine, faBatteryFull, faBolt, faBullseye, faCar, faCartShopping, faCircleQuestion, faClipboardList, faClock,
  faFire, faHandHoldingDollar, faHouse, faLayerGroup, faPalette, faPlug, faPlus, faScrewdriverWrench, faSeedling,
  faSolarPanel, faTruck, faUserGear, faWallet,
} from '@fortawesome/free-solid-svg-icons'
import { magazineManufacturers } from '../filtersSchema'
import SprzetKlienta from '../components/rynek/SprzetKlienta'
import { PUSTY_SPRZET, maOpisSprzetu } from '../lib/sprzetKlienta'
import useDocumentTitle from '../hooks/useDocumentTitle'
import VariantCard from '../components/kits/VariantCard'
import KreatorSzkielet from '../components/kreator/KreatorSzkielet'
import KreatorKrokowy from '../components/kreator/KreatorKrokowy'
import WyborDrogi from '../components/kreator/WyborDrogi'
import { suggestKits } from '../api/kits'
import { useLimitBezKonta } from '../hooks/useLimitBezKonta'
import OknoRejestracji from '../components/auth/OknoRejestracji'
import { podzielWarianty, TEKSTY_OKNA_DOBORU, ZestawyZaKontem } from '../components/kits/ZestawyBezKonta'
import { useTranslation, Trans } from 'react-i18next'
import i18n from '../i18n'
import { KRAJ, jestPolska } from '../kraj'
import { UZYSK_KWH_NA_KWP_KRAJU } from '../lib/mocKalkulator'
import { pln } from '../components/kits/format'
import ZwykleCeny from '../components/kits/ZwykleCeny'
import { kontoSzkicu, zapiszSzkic } from '../lib/rynek'
import { useAuth } from '../context/AuthContext'
import { zdarzenie } from '../lib/pomiar'
import {
  BUDZET_MIN, celeKraju, CZESCI_ROZBUDOWY, czesciRozbudowy, potrzebaFaz, celeWymagajaMagazynu, jestRozbudowa,
  planMagazynu, wymagaMagazynu, zbudujZapytanie, moznaLuzowacEstetyke, uporzadkujWarianty, skutkiCelow,
  odpowiedziZAdresu, wybraneCele, parametryZwyklychCen, wyborDoboru, zapiszWyborDoboru, odczytajWyborDoboru,
  opcjaZOdpowiedzi, obecnaInstalacja, nowyFalownikRozbudowy, WYBORY_DOKLADKI,
  maWlasnySprzet, dobieramyZestaw, szkicDoboru, wyczyscWyborDoboru,
} from '../lib/doborCele'
import { opisWyboru } from '../lib/rynek'
import { kluczKraju } from '../kraj'

// Logika doboru jest w `lib/doborCele`; eksport zostaje dla testów i miejsc, które brały te funkcje ze strony.
export { zbudujZapytanie, roczneZuzycie, kosztyKonstrukcjiIMontazu, odpowiedziZAdresu } from '../lib/doborCele'

/**
 * Asystent zestawu — TRYB PROSTY.
 *
 * ═══ PO CO DRUGA DROGA DO TEGO SAMEGO ═══
 * Pełny asystent (`/dobor-zestawu-pv`) pyta o parametry: moc instalacji, zakres
 * mocy falownika, pojemność magazynu, filtry zaawansowane. To dobre narzędzie
 * dla instalatora i dla kogoś, kto już wie, czego chce. Osoba, która pierwszy
 * raz myśli o fotowoltaice, nie zna żadnej z tych liczb i odbija się od
 * pierwszego pola.
 *
 * Ten tryb pyta o RACHUNEK I CELE (makieta „VoltValue dla domu”, D2, 2026-10-24), a wszystkie parametry
 * wylicza sam (`lib/doborCele`). Pod spodem
 * jedzie ten sam silnik (`POST /api/v1/kits/suggest`) i te same karty wariantów,
 * więc nie ma dwóch źródeł prawdy o tym, co jest dobrym zestawem — jest jedno,
 * z dwoma wejściami.
 *
 * ═══ SKĄD BIORĄ SIĘ LICZBY ═══
 * Moc instalacji z rocznego zużycia tą samą regułą co kalkulator mocy i karta klienta
 * (`mocZZuzycia` w `lib/klienci/energia.js`, czyli `obliczMoc`): zużycie przez uzysk dachu na
 * południe (ok. 1000 kWh z 1 kWp), razy mnożnik najczęstszego profilu „rano i wieczorem”
 * (bez magazynu 125%, z magazynem prawie 100%), w pełnych modułach. Wcześniej był tu osobny
 * wzór „zużycie / 1000”, przez co ten sam dom dostawał inną moc niż w kalkulatorze.
 * Pojemność magazynu liczymy jak w kreatorze magazynów: z udziału zużycia dobowego
 * zależnego od celu.
 *
 * Obie reguły są przybliżeniami i mówimy o tym wprost na ekranie, zamiast
 * udawać precyzję, której przy jednym pytaniu o rachunek nie ma.
 */

// 1 kWp instalacji w Polsce daje około 1000 kWh rocznie (uzysk 950-1050 zależnie
// od nachylenia i zacienienia); pokazywane w objaśnieniu wyniku. Wartość zależy
// od kraju (lib/mocKalkulator).
const UZYSK_KWH_NA_KWP = UZYSK_KWH_NA_KWP_KRAJU

// Przykład kwoty w pytaniu o budżet: Polska bez zmian, inne kraje kursem orientacyjnym 1 PLN ≈ 1,18 RON.
const BUDZET_PRZYKLAD = jestPolska ? 40000 : 47000

// Marki do pytań o obecny sprzęt (ta sama lista co filtry katalogu) i „Inna albo nie wiem”.
const opcjeMarek = (marki, t) => [
  ...Object.keys(marki).sort((a, b) => a.localeCompare(b, 'pl')).map((m) => ({ v: m, l: m })),
  { v: 'inna', l: t('prosty.rozbudowa.inna') },
]
const IKONY_CZESCI = { moduly: faSolarPanel, falownik: faPlug, magazyn: faBatteryFull }

// Pytania budowane przy renderze, bo teksty pochodzą ze słownika `asystent`. Kolejność (decyzja 2026-10-26):
// czy instalacja PV już jest, kto wybiera sprzęt (i kto kupuje), przy rozbudowie zaznaczone części, rachunek, cele
// albo pytania o zaznaczone części, dopytania tylko gdy potrzebne (fazy, miejsce na moduły, magazyn) i budżet.
// `osadzony` (okno nad Projektem 3D): bez pytań o sprzęt, bo tam wybiera się zestaw do projektu.
/**
 * Zestawy w zleceniu (runda 9): najwyżej dwa, „Najtańszy zestaw” i „Najwyższa jakość” (warianty silnika `cheapest`
 * i `quality`). Bez wariantu jakości tylko najtańszy. Zwraca `[{ v, tytul }]`; poza zleceniem tytuł z silnika.
 */
const dwaZestawy = (warianty) => {
  const najtanszy = warianty.find((v) => v.variant === 'cheapest') || warianty[0]
  const jakosc = warianty.find((v) => v.variant === 'quality' && v !== najtanszy)
  return [
    ...(najtanszy ? [{ v: najtanszy, tytul: i18n.t('asystent:prosty.zlecenie.zestaw_najtanszy') }] : []),
    ...(jakosc ? [{ v: jakosc, tytul: i18n.t('asystent:prosty.zlecenie.zestaw_jakosc') }] : []),
  ]
}

// Ikony kafli odpowiedzi (kreator krokowy; w zwykłym kreatorze kafle są bez ikon).
const IKONY_KTO = { wykonawca: faUserGear, voltvalue: faLayerGroup, wlasny: faClipboardList }
const IKONY_CELOW = {
  rachunek: faWallet, zwrot: faChartLine, awaria: faBolt, auto: faCar, pompa: faFire, estetyka: faPalette, jakosc: faAward,
  dotacja: faHandHoldingDollar, szybki_montaz: faClock,
}

// Droga „Kilka pytań” w formularzu zlecenia (decyzja 2026-10-26): wymagane są tylko te, resztę da się pominąć.
// Magazyn to kluczowa decyzja, więc trzeci krok przed rozdrożem (runda 9, 2026-10-26); przy rozbudowie jest ukryty.
// Przy rozbudowie wymagane jest „Co chcesz rozbudować?” (prośba właściciela 2026-10-30): bez odpowiedzi
// `czesciRozbudowy` przyjmowało sam magazyn. Wymagane liczą się tylko wtedy, gdy pytanie jest widoczne.
export const WYMAGANE_W_ZLECENIU = ['masz_pv', 'czesci', 'zuzycie', 'magazyn']

/** Sprzęt klienta jednym zdaniem (oś po prawej, podsumowanie): to, co podał przy modułach, falowniku i magazynie. */
const krotkoSprzet = (s) => [[s?.panel, s?.uwagi?.moduly, 'W'], [s?.falownik, s?.uwagi?.falownik, 'kW'], [s?.magazyn, s?.uwagi?.magazyn, 'kWh']]
  .map(([w, u, j]) => opisWyboru(w, j) || String(u || '').trim()).filter(Boolean).join('; ')

const zbudujPytania = (t, { osadzony = false, trybZlecenia = false } = {}) => {
  const czesc = (c) => (o) => czesciRozbudowy(o).has(c)
  const liczbaRozbudowy = (klucz, jednostka, krok) => ({
    klucz, ikona: faSolarPanel, typ: 'liczba', opcjonalne: true,
    etykieta: t(`prosty.rozbudowa.${klucz}_etykieta`), tytul: t(`prosty.rozbudowa.${klucz}_tytul`),
    opis: t(`prosty.rozbudowa.${klucz}_opis`), tekstPominiecia: t(`prosty.rozbudowa.${klucz}_pominiecie`),
    liczba: { jednostka, min: 1, krok },
  })
  const lista = [
    {
      klucz: 'masz_pv', ikona: faSolarPanel, etykieta: t('prosty.instalacja.etykieta'),
      // W podsumowaniu przy rozbudowie „Rozbudowa instalacji PV” (J4).
      etykietaOd: (o) => (o.masz_pv === 'tak' ? t('prosty.instalacja.etykieta_rozbudowa') : null),
      tytul: t('prosty.instalacja.tytul'), opis: t('prosty.instalacja.opis'),
      opcje: [
        { v: 'nie', l: t('prosty.instalacja.nie'), ikona: faHouse },
        { v: 'tak', l: t('prosty.instalacja.tak'), ikona: faPlus },
      ],
    },
    {
      klucz: 'kto_wybiera', ikona: faScrewdriverWrench, etykieta: t('prosty.kto.etykieta'),
      tytul: t('prosty.kto.pytanie'), opis: t('prosty.kto.pomoc'),
      warunek: () => !osadzony,
      // „Mam już wybrany sprzęt” (decyzja 2026-10-26): karty sprzętu zamiast zestawów.
      opcje: ['wykonawca', 'voltvalue', 'wlasny'].map((v) => ({ v, l: t(`prosty.kto.${v}`), o: t(`prosty.kto.${v}_opis`), ikona: IKONY_KTO[v] })),
    },
    {
      klucz: 'kto_kupuje', ikona: faCartShopping, etykieta: t('prosty.kto.kupuje_etykieta'),
      tytul: t('prosty.kto.kupuje_pytanie'), opis: t('prosty.kto.kupuje_pomoc'),
      warunek: (o) => !osadzony && (o.kto_wybiera === 'voltvalue' || o.kto_wybiera === 'wlasny'),
      opcje: ['wykonawca', 'klient'].map((v) => ({
        v, l: t(`prosty.kto.kupuje_${v}`), o: t(`prosty.kto.kupuje_${v}_opis`), ikona: v === 'klient' ? faCartShopping : faTruck,
      })),
    },
    {
      klucz: 'sprzet_klienta', ikona: faScrewdriverWrench, etykieta: t('prosty.sprzet_klienta.etykieta'),
      tytul: t('prosty.sprzet_klienta.tytul'), opis: t('prosty.sprzet_klienta.opis'),
      // „Dalej” dopiero z choć jedną informacją; pominięte karty oddają decyzję o sprzęcie wykonawcy (`zWyboremSprzetu`).
      typ: 'komponent', pusta: PUSTY_SPRZET, gotowe: maOpisSprzetu,
      warunek: (o) => !osadzony && maWlasnySprzet(o),
      render: ({ wartosc, onZmiana }) => <SprzetKlienta wartosc={wartosc} onZmiana={onZmiana} wlasnyZakres bezOpisu />,
      opisOdpowiedzi: krotkoSprzet,
    },
    {
      klucz: 'czesci', ikona: faScrewdriverWrench, etykieta: t('prosty.czesci.etykieta'),
      tytul: t('prosty.czesci.pytanie'), opis: t('prosty.czesci.pomoc'),
      typ: 'wielokrotny', kafle: true,
      warunek: (o) => jestRozbudowa(o),
      opcje: CZESCI_ROZBUDOWY.map((c) => ({ v: c, l: t(`prosty.czesci.${c}`), o: t(`prosty.czesci.${c}_opis`), ikona: IKONY_CZESCI[c] })),
    },
    {
      klucz: 'zuzycie', ikona: faWallet, etykieta: t('prosty.zuzycie.etykieta'),
      tytul: t('prosty.zuzycie.tytul'),
      // Kto zna roczne zużycie w kWh, ma liczbę DOKŁADNIEJSZĄ od rachunku, bo rachunek przeliczamy przez średnią cenę.
      opis: t('prosty.zuzycie.opis'),
      typ: 'liczba',
      jednostki: [
        { v: 'kwh', l: t('prosty.zuzycie.kwh.l'), jednostka: 'kWh', placeholder: t('prosty.zuzycie.kwh.placeholder') },
        // `v: 'zl'` to klucz odpowiedzi (rachunek w walucie kraju), nie waluta.
        {
          v: 'zl',
          l: t('prosty.zuzycie.rachunek.l', { waluta: KRAJ.symbol }),
          jednostka: t('prosty.zuzycie.rachunek.jednostka', { waluta: KRAJ.symbol }),
          placeholder: t('prosty.zuzycie.rachunek.placeholder'),
        },
      ],
      liczba: { jednostka: 'kWh', min: 50, krok: 50 },
    },
    {
      klucz: 'cele', ikona: faBullseye, etykieta: t('prosty.cele.pytanie'),
      tytul: t('prosty.cele.pytanie'), opis: t('prosty.cele.pomoc'),
      typ: 'wielokrotny',
      warunek: (o) => !jestRozbudowa(o),
      // Runda 9 (2026-10-26): cele bez podpisów; planowana pompa i auto zawsze dokładają zużycie (bez dopytania).
      opcje: celeKraju().map((c) => ({ v: c.klucz, l: t(`prosty.cele.${c.klucz}`), ikona: IKONY_CELOW[c.klucz] })),
    },
    // Moduły (runda 6, 2026-10-26): wybór dokładki wprost; „Nie wiem” liczy ją z zużycia i obecnej mocy. Bez pytania
    // o miejsce (kto go nie ma, nie zaznacza modułów) i bez pytań o obecny falownik (prawie zawsze idzie do wymiany).
    {
      klucz: 'dokladka', ikona: faSolarPanel, etykieta: t('prosty.rozbudowa.dokladka_etykieta'),
      tytul: t('prosty.rozbudowa.dokladka_tytul'), opis: t('prosty.rozbudowa.dokladka_opis'),
      warunek: czesc('moduly'),
      opcje: [
        ...WYBORY_DOKLADKI.map((v) => ({ v, l: `+${v} kWp` })),
        { v: 'wlasna', l: t('prosty.rozbudowa.dokladka_wlasna') },
        { v: 'nie_wiem', l: t('prosty.rozbudowa.dokladka_nie_wiem') },
      ],
    },
    {
      ...liczbaRozbudowy('dokladka_kwp', 'kWp', 0.5), opcjonalne: false,
      warunek: (o) => czesc('moduly')(o) && o.dokladka === 'wlasna',
    },
    { ...liczbaRozbudowy('obecna_moc', 'kWp', 0.1), warunek: (o) => czesc('moduly')(o) && o.dokladka === 'nie_wiem' },
    {
      klucz: 'nowy_falownik', ikona: faPlug, etykieta: t('prosty.rozbudowa.nowy_etykieta'),
      tytul: t('prosty.rozbudowa.nowy_tytul'), opis: t('prosty.rozbudowa.nowy_opis'),
      // Z magazynem nowy falownik i tak musi być hybrydowy, więc nie pytamy.
      warunek: (o) => czesc('falownik')(o) && !czesc('magazyn')(o),
      opcje: [
        { v: 'hybrydowy', l: t('prosty.rozbudowa.nowy_hybrydowy') },
        { v: 'zwykly', l: t('prosty.rozbudowa.nowy_mocniejszy') },
        { v: 'nie_wiem', l: t('prosty.rozbudowa.nie_wiem') },
      ],
    },
    {
      klucz: 'masz_magazyn', ikona: faBatteryFull, etykieta: t('prosty.rozbudowa.masz_tytul'),
      tytul: t('prosty.rozbudowa.masz_tytul'), opis: t('prosty.rozbudowa.masz_opis'),
      warunek: czesc('magazyn'),
      opcje: [
        { v: 'tak', l: t('prosty.rozbudowa.tak') },
        { v: 'nie', l: t('prosty.rozbudowa.nie') },
      ],
    },
    {
      klucz: 'marka_magazynu', ikona: faBatteryFull, etykieta: t('prosty.rozbudowa.magazyn_marka_etykieta'),
      tytul: t('prosty.rozbudowa.magazyn_marka_tytul'), opis: t('prosty.rozbudowa.magazyn_marka_opis'),
      typ: 'lista',
      warunek: (o) => czesc('magazyn')(o) && o.masz_magazyn === 'tak',
      opcje: opcjeMarek(magazineManufacturers, t),
    },
    {
      klucz: 'falownik_hybrydowy', ikona: faPlug, etykieta: t('prosty.rozbudowa.hybryda_tytul'),
      tytul: t('prosty.rozbudowa.hybryda_tytul'), opis: t('prosty.rozbudowa.hybryda_opis'),
      // Przy wymianie falownika nowy i tak będzie hybrydowy.
      warunek: (o) => czesc('magazyn')(o) && !czesc('falownik')(o) && o.masz_magazyn === 'nie',
      opcje: [
        { v: 'tak', l: t('prosty.rozbudowa.hybryda_tak') },
        { v: 'nie', l: t('prosty.rozbudowa.hybryda_nie') },
        { v: 'nie_wiem', l: t('prosty.rozbudowa.nie_wiem') },
      ],
    },
    {
      klucz: 'fazy', ikona: faPlug, etykieta: t('prosty.fazy.etykieta'),
      tytul: t('prosty.fazy.tytul'), opis: t('prosty.fazy.opis'),
      // Tylko gdy ma znaczenie (auto, pompa ciepła, instalacja ponad 6 kWp); inaczej silnik dostaje obie grupy.
      // W rozbudowie zawsze, gdy dobieramy nowy falownik (runda 6).
      warunek: (o) => (jestRozbudowa(o) ? nowyFalownikRozbudowy(o) : potrzebaFaz(o)),
      opcje: [
        { v: '1', l: t('prosty.fazy.1.l'), o: t('prosty.fazy.1.o'), ikona: faPlug },
        { v: '3', l: t('prosty.fazy.3.l'), o: t('prosty.fazy.3.o'), ikona: faBolt },
        { v: 'nie_wiem', l: t('prosty.fazy.nie_wiem.l'), o: t('prosty.fazy.nie_wiem.o'), ikona: faCircleQuestion },
      ],
    },
    {
      klucz: 'montaz', ikona: faHouse, etykieta: t('prosty.montaz.etykieta'),
      tytul: t('prosty.montaz.tytul'), opis: t('prosty.montaz.opis'),
      warunek: (o) => !jestRozbudowa(o),
      opcje: [
        // J2 (2026-10-26): na dachu bez podpisu, na gruncie dwustronne jako preferencja, „Zdecyduję później” bez filtra.
        { v: 'dach', l: t('prosty.montaz.dach.l'), ikona: faHouse },
        { v: 'grunt', l: t('prosty.montaz.grunt.l'), o: t('prosty.montaz.grunt.o'), ikona: faSeedling },
        { v: 'pozniej', l: t('prosty.montaz.pozniej.l'), o: t('prosty.montaz.pozniej.o'), ikona: faCircleQuestion },
      ],
    },
    {
      klucz: 'magazyn', ikona: faBatteryFull, etykieta: t('prosty.magazyn.etykieta'),
      tytul: t('prosty.magazyn.tytul'), opis: t('prosty.magazyn.opis'),
      // Pytanie zawsze przy nowej instalacji (runda 9): cel, który wymaga magazynu („Prąd przy awarii”, „Dotacja”),
      // tylko wstępnie zaznacza „Tak” i mówi dlaczego; jawne „Nie” klienta wygrywa (`lib/doborCele.wymagaMagazynu`).
      warunek: (o) => !jestRozbudowa(o),
      domyslna: (o) => (celeWymagajaMagazynu(o) ? 'tak' : undefined),
      dopisek: (o) => (celeWymagajaMagazynu(o)
        ? t('prosty.magazyn.z_celu', { cele: ['awaria', 'dotacja'].filter((c) => wybraneCele(o).has(c)).map((c) => t(`prosty.cele.${c}`)).join(', ') })
        : null),
      opcje: [
        { v: 'tak', l: t('prosty.magazyn.tak.l'), o: t('prosty.magazyn.tak.o'), ikona: faBatteryFull },
        { v: 'nie', l: t('prosty.magazyn.nie.l'), o: t('prosty.magazyn.nie.o'), ikona: faBan },
        { v: 'nie_wiem', l: t('prosty.magazyn.nie_wiem.l'), o: t('prosty.magazyn.nie_wiem.o'), ikona: faLayerGroup },
      ],
    },
    {
      klucz: 'budzet', ikona: faWallet, etykieta: t('prosty.budzet.etykieta'),
      tytul: t('prosty.budzet.tytul'), opis: t('prosty.budzet.opis'),
      typ: 'liczba', opcjonalne: true,
      // Wyjście nazywa SKUTEK, a nie czynność: większość ludzi budżetu na tym etapie nie zna.
      tekstPominiecia: t('prosty.budzet.pominiecie'),
      liczba: {
        jednostka: KRAJ.symbol, min: BUDZET_MIN, krok: 1000,
        placeholder: t('prosty.budzet.placeholder', { kwota: BUDZET_PRZYKLAD }),
      },
    },
  ]
  if (!trybZlecenia) return lista
  // „Pomiń” przy każdym pytaniu poza wymaganymi: kto pominie, ten zostawia decyzję wykonawcy.
  return lista.map((p) => (WYMAGANE_W_ZLECENIU.includes(p.klucz) || p.opcjonalne ? p
    : { ...p, opcjonalne: true, tekstPominiecia: t('prosty.zlecenie.pomin') }))
}

/** Sprzęt klienta z odpowiedzi do szkicu zlecenia: tylko przy „Mam już wybrany sprzęt” i gdy coś podał. */
const wlasnySprzet = (o) => (maWlasnySprzet(o) && maOpisSprzetu(o?.sprzet_klienta) ? o.sprzet_klienta : null)

// Odpowiedzi ostatniego doboru w pamięci karty: „Wróć do doboru” i „Zmień zestaw” na potwierdzeniu wracają tutaj.
const KLUCZ_ODPOWIEDZI = kluczKraju('vv:dobor-prosty-odpowiedzi')
export const ADRES_POWROTU = '/dobierz-zestaw?powrot=1'
const powrotDoDoboru = (search) => new URLSearchParams(search).get('powrot') === '1'
// „Zmień odpowiedzi” z formularza zlecenia (G4): po doborze przycisk wraca do zlecenia ze świeżymi odpowiedziami.
const zFormularzaZlecenia = (search) => new URLSearchParams(search).get('do') === 'zlecenie'
const zapiszOdpowiedzi = (o) => {
  try { sessionStorage.setItem(KLUCZ_ODPOWIEDZI, JSON.stringify(o)) } catch { /* bez pamięci karty */ }
}
const odczytajOdpowiedzi = () => {
  try { return JSON.parse(sessionStorage.getItem(KLUCZ_ODPOWIEDZI) || 'null') } catch { return null }
}

/**
 * „Twoje zlecenie” (decyzja 2026-10-26): przy „Wykonawca zaproponuje sprzęt” odpowiedzi to brief dla wykonawców,
 * więc pokazujemy je jako zlecenie zamiast zestawów ze sklepów. Wiersze bez odpowiedzi pomijamy.
 */
const TwojeZlecenie = ({ o, wyliczenia }) => {
  const { t } = useTranslation('asystent')
  const liczba = (v) => String(v).replace('.', ',')
  const rozbudowa = jestRozbudowa(o)
  const czesci = CZESCI_ROZBUDOWY.filter((c) => czesciRozbudowy(o).has(c))
  const cele = [...wybraneCele(o)]
  const magazyn = wyliczenia.pojemnosc
    ? t('prosty.twoje_zlecenie.magazyn_wartosc', { kwh: liczba(wyliczenia.pojemnosc) })
    : o?.magazyn === 'nie_wiem' ? t('prosty.twoje_zlecenie.magazyn_do_decyzji')
      : rozbudowa ? null : t('prosty.twoje_zlecenie.magazyn_bez')
  const wiersze = [
    rozbudowa && czesci.length ? ['rozbudowa', czesci.map((c) => t(`prosty.czesci.${c}`)).join(', ')] : null,
    rozbudowa && obecnaInstalacja(o) ? ['obecna', obecnaInstalacja(o)] : null,
    wyliczenia.kwp ? ['moc', t(rozbudowa ? 'prosty.twoje_zlecenie.dokladka_wartosc' : 'prosty.twoje_zlecenie.moc_wartosc', { kwp: liczba(wyliczenia.kwp) })] : null,
    wyliczenia.rok ? ['zuzycie', t('prosty.twoje_zlecenie.zuzycie_wartosc', { kwh: wyliczenia.rok.toLocaleString(KRAJ.locale) })] : null,
    magazyn ? ['magazyn', magazyn] : null,
    cele.length ? ['cele', cele.map((c) => t(`prosty.cele.${c}`)).join(', ')] : null,
    o?.fazy === '1' || o?.fazy === '3' ? ['fazy', t('prosty.twoje_zlecenie.fazy_wartosc', { fazy: o.fazy })] : null,
    o?.montaz ? ['montaz', t(`prosty.montaz.${o.montaz}.l`)] : null,
    maWlasnySprzet(o) && krotkoSprzet(o.sprzet_klienta) ? ['sprzet', krotkoSprzet(o.sprzet_klienta)] : null,
    ['budzet', o?.budzet ? pln(o.budzet) : t('prosty.twoje_zlecenie.budzet_brak')],
  ].filter(Boolean)
  return (
    <section className="mb-5 rounded-2xl border border-gray-200 bg-white px-5 py-4" aria-labelledby="twoje-zlecenie">
      <h2 id="twoje-zlecenie" className="text-xl font-bold text-gray-900">{t('prosty.twoje_zlecenie.tytul')}</h2>
      <p className="mt-1 text-sm text-gray-500">{t('prosty.twoje_zlecenie.opis')}</p>
      <dl className="mt-3 divide-y divide-gray-100">
        {wiersze.map(([k, v]) => (
          <div key={k} className="flex flex-wrap justify-between gap-x-4 py-2 text-sm">
            <dt className="text-gray-500">{t(`prosty.twoje_zlecenie.${k}`)}</dt>
            <dd className="font-semibold text-gray-900 text-right">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/**
 * `osadzony`: ten sam dobór w oknie nad Projektem 3D (uwagi przeglądu A5 i A6, 2026-10-24): bez wyboru drogi nad
 * pytaniami i bez zmiany tytułu karty, odpowiedzi startowe z modelu (`odpowiedziStartowe`), a przycisk zestawu
 * „Użyj w projekcie” oddaje wariant do `onUzyjZestawu` zamiast przechodzić do wykonawcy.
 */
/**
 * `trybZlecenia`: droga „Kilka pytań” osadzona w formularzu zlecenia na Rynku (decyzja 2026-10-26). Bez nagłówka,
 * osi i wyboru drogi, wymagane tylko `WYMAGANE_W_ZLECENIU`, reszta z „Pomiń”. Koniec to `onGotowe(szkic)` ze szkicem
 * z `lib/doborCele.szkicDoboru`: od razu przy „Wykonawca zaproponuje” i „Mam już wybrany sprzęt”, a przy zestawie po
 * wyborze karty („Dalej: gdzie i ile ofert”). `poczatkowe`: odpowiedzi startowe (bez nich zapisane z `/dobierz-zestaw`).
 */
const DoborZestawuProsty = ({
  osadzony = false, odpowiedziStartowe = null, onUzyjZestawu = null,
  trybZlecenia = false, onGotowe = null, poczatkowe: poczatkoweZlecenia = null,
}) => {
  const { t } = useTranslation('asystent')
  useDocumentTitle(trybZlecenia ? false : osadzony ? 'Projekt 3D' : t('prosty.tytul_strony'))
  const pytania = useMemo(() => zbudujPytania(t, { osadzony, trybZlecenia }), [t, osadzony, trybZlecenia])
  const { search } = useLocation()
  const navigate = useNavigate()
  // Szkic i wybór doboru należą do konta, które je zapisało (uwagi przeglądu D12).
  const { isAuthenticated, profil } = useAuth()
  const konto = kontoSzkicu(isAuthenticated, profil)
  const [odpowiedzi, setOdpowiedzi] = useState(null)
  // Odpowiedzi z adresu (strona główna, „VoltValue dla domu”) mają pierwszeństwo. Bez nich wznawiamy ostatnie odpowiedzi
  // z tej karty: powrót z potwierdzenia (`?powrot=1`) i cofnięcie w przeglądarce nie zaczynają od pustego kreatora.
  // W oknie nad Projektem 3D wybiera się zestaw do projektu, więc pytań o sprzęt nie ma.
  const [domyslne] = useState(() => (osadzony
    ? { kto_wybiera: 'voltvalue', kto_kupuje: 'wykonawca', ...(odpowiedziStartowe || {}) }
    : trybZlecenia ? {} : odpowiedziZAdresu(search)))
  // Bez odpowiedzi w tej karcie (np. formularz zlecenia w nowej karcie) zapas z wyboru doboru zapisanego na 3 dni (G4).
  const [poczatkowe] = useState(() => (trybZlecenia
    ? poczatkoweZlecenia || odczytajOdpowiedzi() || odczytajWyborDoboru(Date.now(), konto)?.odpowiedzi || null
    : !osadzony && (powrotDoDoboru(search) || !Object.keys(odpowiedziZAdresu(search)).length)
      ? odczytajOdpowiedzi() || (powrotDoDoboru(search) ? odczytajWyborDoboru(Date.now(), konto)?.odpowiedzi || null : null)
      : null))
  const doZleceniaWroc = !osadzony && !trybZlecenia && zFormularzaZlecenia(search)
  // Bieżące odpowiedzi (z `onZmiana`): napis przycisku zależy od tego, czy na końcu są zestawy.
  const [biezace, setBiezace] = useState(null)
  const zmianaOdpowiedzi = (o) => { setBiezace(o); zapiszOdpowiedzi(o) }
  // W trybie zlecenia brak odpowiedzi o sprzęt znaczy to samo co „Pomiń”: decyzję podejmie wykonawca.
  // „Mam już wybrany sprzęt” bez żadnej informacji o sprzęcie to też decyzja wykonawcy.
  const zWyboremSprzetu = (o) => {
    if (trybZlecenia && o && o.kto_wybiera === undefined) return { ...o, kto_wybiera: null }
    if (o && maWlasnySprzet(o) && !wlasnySprzet(o)) return { ...o, kto_wybiera: null }
    return o
  }
  // Bez konta (FE-08): jeden dobór w tej przeglądarce (wspólny licznik `asystent` z pełnym asystentem) i w każdej
  // grupie widoczny najtańszy zestaw. Przy „pokaż obie opcje” to dwa zestawy: z magazynem i bez, bo o to pytał.
  const limitDoboru = useLimitBezKonta('asystent')
  const { bezKonta } = limitDoboru
  const [oknoKonta, setOknoKonta] = useState(null)
  // Grupy wyników: jedna przy zwykłej odpowiedzi, dwie przy „pokaż obie opcje".
  const [grupy, setGrupy] = useState([])
  const [wyliczenia, setWyliczenia] = useState(null)
  // „Wykonawca zaproponuje sprzęt”: zamiast zestawów podsumowanie zlecenia (decyzja 2026-10-26).
  const [podsumowanie, setPodsumowanie] = useState(false)
  const wynikiRef = useRef(null)

  useEffect(() => {
    if (grupy.length || podsumowanie) {
      setTimeout(() => wynikiRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
    }
  }, [grupy, podsumowanie])

  // Zapis wyboru dla formularza zlecenia i pomiar, wspólne dla obu wyników.
  const zapiszWynik = (o, wyl) => {
    setOdpowiedzi(o)
    zdarzenie('dobor_cele', [...wybraneCele(o)].sort().join(','))
    const opcja = opcjaZOdpowiedzi(o)
    if (opcja) zdarzenie('dobor_sprzet', opcja.v)
    // Wybór czeka na formularz zlecenia (P3/P0) także po rejestracji.
    // Surowe odpowiedzi też: „Zmień odpowiedzi” z formularza zlecenia wznawia pytania także w innej karcie (G4).
    zapiszWyborDoboru({ ...wyborDoboru(o, wyl), odpowiedzi: o }, Date.now(), konto)
  }

  /**
   * „Pokaż mi obie opcje” ZNACZY dwa zestawy odpowiedzi (z magazynem i bez), a nie jeden: dwa zapytania równolegle
   * i dwie grupy wyników (`planMagazynu`). Przy rozbudowie magazynu zawsze jedna grupa, z magazynem.
   */
  const dobierz = async (odp) => {
    const o = zWyboremSprzetu(odp)
    if (!dobieramyZestaw(o)) {
      // „Wykonawca zaproponuje” i „Mam już wybrany sprzęt”: bez silnika i bez limitu doboru, tylko moc, magazyn
      // i koszty do podsumowania i zwykłych cen.
      const z = zbudujZapytanie(o, wymagaMagazynu(o))
      const wyl = { kwp: z.kwp, rok: z.rok, pojemnosc: z.pojemnosc, budzet: o.budzet || null, koszty: z.koszty }
      setWyliczenia({ ...wyl, skutki: [], zwykleCeny: parametryZwyklychCen(o, wyl, Boolean(wyl.pojemnosc)) })
      zapiszWynik(o, wyl)
      if (trybZlecenia) {
        onGotowe?.(szkicDoboru(o, wyl, { sprzetKlienta: wlasnySprzet(o) }))
        return { ok: true, data: { propozycje: [], kandydatow: 0, komunikat: null } }
      }
      setGrupy([])
      setPodsumowanie(true)
      return { ok: true, data: { propozycje: [], kandydatow: 0, komunikat: null } }
    }
    setPodsumowanie(false)
    // W zleceniu bez limitu i blokady „na darmowym koncie”: konto i tak powstaje przy wystawieniu zlecenia (runda 9).
    if (limitDoboru.wyczerpany && !trybZlecenia) {
      setOknoKonta('limit')
      return { ok: false, error: TEKSTY_OKNA_DOBORU.limit.opis }
    }
    // Rozbudowa: jedno zapytanie (zaznaczone części przesądzają o magazynie).
    const plan = jestRozbudowa(o) ? [wymagaMagazynu(o)] : planMagazynu(o)
    const przebiegi = plan.map(async (zMag) => {
      let z = zbudujZapytanie(o, zMag)
      let res = await suggestKits(z.zapytanie)
      let luzowanaEstetyka = false
      // Estetyka: gdy z samymi modułami full black nic nie wchodzi, drugi raz z full black jako preferencją.
      if (moznaLuzowacEstetyke(o) && res && !res.error && !(res.variants || []).length) {
        z = zbudujZapytanie(o, zMag, { luzujEstetyke: true })
        res = await suggestKits(z.zapytanie)
        luzowanaEstetyka = true
      }
      return { res, ...z, zMag, luzowanaEstetyka }
    })
    const wyniki = await Promise.all(przebiegi)

    // Wystarczy JEDEN udany przebieg: przy „obu opcjach" bywa, że z magazynem
    // nic nie wchodzi w budżet, a bez magazynu wchodzi. Odrzucenie całości
    // ukryłoby przed użytkownikiem właśnie tę odpowiedź, po którą przyszedł.
    const udane = wyniki.filter((w) => w.res && !w.res.error)
    if (udane.length === 0) {
      return { ok: false, error: t('prosty.blad') }
    }

    const pierwszy = udane[0]
    const wyl = { kwp: pierwszy.kwp, rok: pierwszy.rok,
                  pojemnosc: udane.find((w) => w.zMag)?.pojemnosc ?? null,
                  budzet: o.budzet || null, koszty: pierwszy.koszty }
    setWyliczenia({
      ...wyl,
      skutki: skutkiCelow(o, { rok: wyl.rok, kwp: wyl.kwp, luzowanaEstetyka: udane.some((w) => w.luzowanaEstetyka) }),
      zwykleCeny: parametryZwyklychCen(o, wyl, Boolean(wyl.pojemnosc)),
    })
    zapiszWynik(o, wyl)
    setGrupy(udane.map((w) => ({
      zMagazynem: w.zMag,
      warianty: uporzadkujWarianty(w.res.variants || [], o),
      zapytanie: w.zapytanie,
    })))
    if (udane.some((w) => w.res.variants?.length)) limitDoboru.sprobuj()
    // Kreator rysuje własną sekcję wyników tylko dla trzech propozycji produktu.
    // Tutaj wyniki to zestawy, więc zwracamy pustą listę i rysujemy je sami niżej.
    return { ok: true, data: { propozycje: [], kandydatow: 0, komunikat: null } }
  }

  const jakiekolwiekWarianty = grupy.some((g) => g.warianty.length > 0)
  const doZlecenia = () => {
    zapiszSzkic(szkicDoboru(odpowiedzi, wyliczenia, { sprzetKlienta: wlasnySprzet(odpowiedzi) }), Date.now(), konto)
    navigate('/rynek')
  }
  // Wariant wybrany w trybie zlecenia: szkic z tym zestawem do formularza („Dalej: gdzie i ile ofert”).
  const zestawDoZlecenia = (w) => onGotowe?.(szkicDoboru(odpowiedzi, wyliczenia, { wariant: w }))

  // „Zacznij od nowa” (J7): odpowiedzi z pamięci karty i zapisany wybór doboru tego konta znikają, wynik też.
  const zacznijOdNowa = () => {
    try { sessionStorage.removeItem(KLUCZ_ODPOWIEDZI) } catch { /* bez pamięci karty */ }
    wyczyscWyborDoboru(konto)
    setGrupy([]); setWyliczenia(null); setPodsumowanie(false); setOdpowiedzi(null)
  }

  // Napis przycisku wyniku: bez zestawów („Wykonawca zaproponuje”, własny sprzęt) w trybie zlecenia od razu dalej.
  const napisWyniku = (o) => (trybZlecenia && !dobieramyZestaw(zWyboremSprzetu(o || {})) ? t('prosty.zlecenie.dalej') : t('prosty.przycisk'))

  return (
    <>
      {trybZlecenia ? (
        <KreatorKrokowy
          pytania={pytania}
          wymagane={WYMAGANE_W_ZLECENIU}
          domyslne={domyslne}
          poczatkowe={poczatkowe}
          onZmiana={zmianaOdpowiedzi}
          onDobierz={dobierz}
          tekstPrzycisku={napisWyniku}
          onOdNowa={zacznijOdNowa}
          onEkran={(e) => zdarzenie('rynek_krok', `kreator:${e}`)}
        />
      ) : (
      <KreatorSzkielet
        tytul={t('prosty.tytul')}
        opis={t('prosty.opis')}
        pytania={pytania}
        tekstPrzycisku={napisWyniku(biezace)}
        onDobierz={dobierz}
        domyslne={domyslne}
        poczatkowe={poczatkowe}
        onZmiana={zmianaOdpowiedzi}
        konfiguracjaWynikow={() => ({ kategoria: 'panel', wiersze: [] })}
        nadPytaniami={osadzony ? null : <WyborDrogi aktywna="pytania" />}
      />
      )}

      {podsumowanie && wyliczenia && !trybZlecenia && (
        <div ref={wynikiRef} className="px-4 md:px-8 lg:px-12 pb-16 max-w-6xl mx-auto scroll-mt-6">
          <TwojeZlecenie o={odpowiedzi} wyliczenia={wyliczenia} />
          {wyliczenia.zwykleCeny && <ZwykleCeny parametry={wyliczenia.zwykleCeny} className="mb-5" />}
          <button type="button" onClick={doZlecenia}
                  className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700">
            {doZleceniaWroc ? t('prosty.zlecenie.powrot_przycisk') : t('prosty.twoje_zlecenie.przycisk')}
          </button>
        </div>
      )}

      {!podsumowanie && grupy.length > 0 && (
        <div ref={wynikiRef} className={trybZlecenia ? 'mt-8 scroll-mt-6' : 'px-4 md:px-8 lg:px-12 pb-16 max-w-6xl mx-auto scroll-mt-6'}>
          {/* Przy rozbudowie moc to sama dokładka, więc zdanie o całej instalacji by myliło; mówią o niej skutki niżej. */}
          {/* W trybie zlecenia bez ramki (zasady wyglądu: bez pastelowych ramek z akapitem). */}
          {wyliczenia?.kwp && !jestRozbudowa(odpowiedzi) && (
            <div className={trybZlecenia ? 'mb-5' : 'mb-5 rounded-2xl border border-blue-200 bg-blue-50/60 px-4 py-3'}>
              <p className="text-sm text-gray-700 leading-6">
                <Trans t={t} i18nKey="prosty.wyliczenia.instalacja"
                  values={{ rok: wyliczenia.rok, kwp: String(wyliczenia.kwp).replace('.', ',') }}
                  components={{ b: <strong /> }} />
                {wyliczenia.pojemnosc
                  ? <>{' '}<Trans t={t} i18nKey="prosty.wyliczenia.magazyn"
                      values={{ kwh: String(wyliczenia.pojemnosc).replace('.', ',') }}
                      components={{ b: <strong /> }} /></>
                  : null}.
                {/* Budżet to całość z montażem (patrz `zbudujZapytanie`), a karty niżej pokazują sam sprzęt. */}
                {wyliczenia.budzet && wyliczenia.koszty > 0
                  ? <>{' '}<Trans t={t} i18nKey="prosty.wyliczenia.rozbicie"
                      values={{ koszt: pln(wyliczenia.koszty), sprzet: pln(Math.max(0, wyliczenia.budzet - wyliczenia.koszty)) }}
                      components={{ b: <strong /> }} /></>
                  : null}
              </p>
              <p className="mt-1 text-xs text-gray-500 leading-5">
                <Trans t={t} i18nKey="prosty.wyliczenia.przyblizenie"
                  values={{ uzysk: UZYSK_KWH_NA_KWP }}
                  components={{
                    link: <Link to="/dobor-zestawu-pv" className="font-semibold text-blue-600 hover:text-blue-700" />,
                  }} />
              </p>
            </div>
          )}

          {wyliczenia?.skutki?.length > 0 && (
            <ul className="mb-5 space-y-1.5" aria-label={t('prosty.cele.pytanie')}>
              {wyliczenia.skutki.map((sk) => (
                <li key={sk.klucz} className="flex items-start gap-2 text-sm text-gray-700 leading-6">
                  <span aria-hidden="true"
                        className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${sk.ton === 'ok' ? 'bg-green-500' : 'bg-amber-500'}`} />
                  <span><strong>{t(`prosty.cele.${sk.klucz}`)}:</strong> {t(`prosty.cele.skutek.${sk.tekst}`, sk.wartosci)}</span>
                </li>
              ))}
            </ul>
          )}
          {wyliczenia?.zwykleCeny && <ZwykleCeny parametry={wyliczenia.zwykleCeny} className="mb-5" />}
          {doZleceniaWroc && (
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-700">{t('prosty.zlecenie.powrot_opis')}</p>
              <button type="button" onClick={doZlecenia}
                      className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                {t('prosty.zlecenie.powrot_przycisk')}
              </button>
            </div>
          )}

          {!jakiekolwiekWarianty ? (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-10 text-center">
              <p className="text-sm text-gray-700">
                {t('prosty.brak_zestawu')}
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{t('prosty.wyniki.naglowek')}</h2>
              <p className="text-sm text-gray-500 mb-6">
                {t('prosty.wyniki.opis')}
              </p>

              {/* W zleceniu jedna grupa (z magazynem, gdy jest) i najwyżej dwa zestawy (runda 9). */}
              {(trybZlecenia ? grupy.filter((g) => g.warianty.length).slice(0, 1) : grupy).map((g, gi) => (
                <div key={g.zMagazynem ? 'z' : 'bez'} className={gi > 0 ? 'mt-10' : ''}>
                  {/* Nagłówek grupy TYLKO przy dwóch grupach: przy jednej byłby
                      powtórzeniem odpowiedzi, którą użytkownik właśnie dał. */}
                  {grupy.length > 1 && !trybZlecenia && (
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      {g.zMagazynem ? t('prosty.wyniki.z_magazynem') : t('prosty.wyniki.bez_magazynu')}
                    </h3>
                  )}
                  {g.warianty.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t('prosty.wyniki.brak_w_wariancie')}
                    </p>
                  ) : (
                    <div className={trybZlecenia ? 'grid gap-5 md:grid-cols-2 items-start' : 'grid gap-5 lg:grid-cols-2 xl:grid-cols-3 items-start'}>
                      {(trybZlecenia ? dwaZestawy(g.warianty) : (bezKonta ? podzielWarianty(g.warianty).widoczne : g.warianty).map((v) => ({ v, tytul: null }))).map(({ v, tytul }, i) => (
                        <VariantCard key={`${g.zMagazynem}-${v.variant}`} variant={v} indeks={i}
                                     requestPayload={g.zapytanie} powrot={ADRES_POWROTU} tytul={tytul} uproszczona={trybZlecenia}
                                     onUzyj={trybZlecenia ? zestawDoZlecenia : onUzyjZestawu ? (w) => onUzyjZestawu(w, odpowiedzi) : null}
                                     tekstUzyj={trybZlecenia ? t('prosty.zlecenie.dalej') : 'Użyj w projekcie'} />
                      ))}
                      {bezKonta && !trybZlecenia && (
                        <ZestawyZaKontem ukryte={podzielWarianty(g.warianty).ukryte} onZalozKonto={() => setOknoKonta('zestawy')} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
      {oknoKonta && (
        <OknoRejestracji narzedzie="asystent" {...TEKSTY_OKNA_DOBORU[oknoKonta]} onZamknij={() => setOknoKonta(null)} />
      )}
    </>
  )
}

export default DoborZestawuProsty
